/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  message,
} from 'antd';
import {
  EnvironmentOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import Direccion from 'components/Direccion';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import makeMessager from 'utils/complexFormsMessages';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import {
  deleteFactibilidad,
  getFactibilidad,
  getFactibilidades,
  patchFactibilidad,
  postFactibilidad,
} from 'api/configuracion/factibilidades';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import { ActionsNames, getModelPermission, hasActionPermission } from 'utils/permission/index';
import NumericInput from 'components/NumericInput';
import { getEmpresa, getEmpresas } from 'api/empresas/empresas';
import { InputSearchPro } from 'components/InputSearch';
import BusquedaAvanzadaPadrones from 'views/Recaudacion/Caja/BusquedaAvanzadaPadrones';
import { getPredio, getPredios } from 'api/catastro/predio';

const { TabPane } = Tabs;
const baseURI = 'configuracion/factibilidades';

const models = ['factibilidad'];

const Factibilidades = ({ permission }) => {
  const addressPermission = useMemo(() => getModelPermission('direccion'), []);
  const [form] = Form.useForm();
  const [formInfo] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [formAddress, setFormAddress] = useState();
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [visibleBusquedaPadrones, setVisibleBusquedaPadrones] = useState(false);
  const [predios, setPredios] = useState([]);

  const [empresaFound, setEmpresaFound] = useState([]);
  const [tipoPadron, setTipoPadron] = useState(0);
  const basicKeys = ['general', 'info'];
  const complexForms = [
    { key: 'direccion', formInstance: formAddress, permission: addressPermission }];

  const fetchData = async (page = 1, q = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getFactibilidades({
      ...q,
      page,
    });
    setData(_data);
    setLoading(false);
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  useEffect(() => {
    let mounted = true;
    let _hasAllPermission = true;
    const validatePermission = () => {
      const _hasAllViewPermission = models
        .map((m) => hasActionPermission(m, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        _hasAllPermission = _hasAllViewPermission;
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const [_data] = await Promise.all([
        getFactibilidades({ page: 1 }, true),
      ]);
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    validatePermission();
    if (_hasAllPermission) {
      fetchAll();
    }
    return () => { mounted = false; };
  }, []);

  const fetchEmpresa = async (id) => {
    try {
      setLoading(true);
      if (id) {
        const _empresa = await getEmpresa(id?.id || id);
        if (_empresa) {
          setEmpresaFound([_empresa]);
          setTimeout(() => {
            form.setFieldsValue({
              empresa: _empresa.id,
            });
          });
        } else {
          message.info('No se encontro la empresa');
        }
        setLoading(false);
      }
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const fetchPredio = async (id) => {
    try {
      setLoading(true);
      if (id) {
        const _predio = await getPredio(id?.id || id);
        if (_predio) {
          setPredios([_predio]);
          setTimeout(() => {
            form.setFieldsValue({
              notificacion: _predio.id,
            });
          });
        } else {
          message.info('No se encontro la empresa');
        }
        setLoading(false);
      }
    } catch (error) {
      onError(error, setLoading);
    }
  };
  const resetAndValidate = () => {
    setEmpresaFound([]);
    form.resetFields(['q']);
    form.validateFields(['q']);
  };

  const resetAndValidateFact = () => {
    setPredios([]);
    form.resetFields(['q_predio']);
    form.validateFields(['q_predio']);
  };

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setEmpresaFound([]);
    setPredios([]);
    setCurrentTabKey('general');
  };

  const onClickEdit = async (id) => {
    const key = selectedRowKeys[0] || id;
    const match = await getFactibilidad(key);
    if (match) {
      await fetchEmpresa(match.empresa?.id);
      form.setFieldsValue(match);
    }
    setVisible(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      await formInfo.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      values.ultimo_adeudo_predial = predios?.id;
      const [key] = selectedRowKeys;
      const _data = await (key ? patchFactibilidad(key, values) : postFactibilidad(values));
      if (_data) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([_data.id]);
          }
          await onClickEdit(_data.id);
        } else {
          onCancel();
        }
        setLoading(false);
        return _data;
      }
      setLoading(false);
      return true;
    } catch (err) {
      onError(err, setLoading, [form, formInfo], setCurrentTabKey);
      return false;
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteFactibilidad(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const searchEmpresa = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _empresaFound = await getEmpresas({ q });
        if (_empresaFound.results.length) {
          const [selected] = _empresaFound.results;
          form.setFieldsValue({
            empresa: selected.id,
          });
          setEmpresaFound(selected.id);
        } else if (!_empresaFound.results.length) {
          message.info('Empresa no encontrada');
          setVisibleBusquedaPadrones(true);
        }
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Empresa',
      dataIndex: 'empresa',
      key: 'empresa',
      render: (val) => (val?.nombre_comercial),
      width: 200,
    },
    {
      titleText: 'Fecha de contratacion',
      dataIndex: 'fecha_de_contratacion',
      key: 'fecha_de_contratacion',
      render: (val) => (val?.format?.(DATE_FRONTEND_FORMAT) || val),
      width: 200,
    },
  ];

  const rules = {
    required: [
      { required: true },
    ],
  };

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys, complexForms, currentTabKey, setCurrentTabKey, showMsg,
  );

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);
  const searchPredio = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q_predio } = form.getFieldsValue(['q_predio']);
      if (q_predio) {
        const _predioFound = await getPredios({ q_predio });
        if (_predioFound.results.length) {
          const [selected] = _predioFound.results;
          form.setFieldsValue({
            notificacion: selected,
          });
          setPredios(selected.id);
        } else if (!_predioFound.results.length) {
          message.info('Predio no encontrado');
          setVisibleBusquedaPadrones(true);
        }
      }
      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };
  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible
          ? (
            <Table
              cols={columns}
              mobileColumns={[columns[0], columns[1]]}
              data={data.results || []}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              rowKey="id"
              baseURI={baseURI}
              permission={permission}
              pagination={false}
              paginationCallback={fetchData}
              count={data.count}
              currentPage={currentPage}
              hasAllViewPermission={hasAllViewPermission}
            />
          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container"
                title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} factibilidad`}
                extra={(
                  <FormSubmitControls
                    onFinish={getOnFinish()}
                    onCancel={onCancel}
                    allowSaveAndContinue
                    onCancelText="Cancelar"
                    permission={permission}
                  />
                )}
                bordered={false}
              >
                <Tabs
                  onChange={onChangeTabKey}
                  activeKey={currentTabKey}
                  type="card"
                >
                  <TabPane
                    tab={(
                      <span>
                        <InfoCircleOutlined />
                        Información General
                      </span>
                    )}
                    key="general"
                    forceRender
                  >
                    <Form
                      name="general"
                      layout="vertical"
                      form={form}
                      onFinish={() => onFinish()}
                      scrollToFirstError
                    >
                      <Row gutter={10}>
                        <InputSearchPro
                          tooltip="Búsqueda por razón social"
                          label="Empresa"
                          onClickAdvanced={() => {
                            setTipoPadron(2);
                            setVisibleBusquedaPadrones(true);
                          }}
                          name="q"
                          rules={rules.required}
                          breakPoints={{
                            xs: 24,
                            sm: 24,
                            md: 16,
                          }}
                          inputProps={{
                            onPressEnter: () => searchEmpresa(),
                            onChange: ({ target: { value } }) => {
                              if (!value) {
                                resetAndValidate();
                              }
                            },
                          }}
                          resultProps={{
                            breakPoints: {
                              xs: 24,
                              sm: 24,
                              md: 16,
                            },
                            name: 'empresa',
                            label: 'Empresa',
                            dataSource: empresaFound,
                            labelProp: 'nombre_comercial',
                            onClear: resetAndValidate,
                          }}
                        />
                        <InputSearchPro
                          tooltip="Búsqueda de notificacion"
                          label="Notificacion"
                          onClickAdvanced={() => {
                            setTipoPadron(3);
                            setVisibleBusquedaPadrones(true);
                          }}
                          name="q_predio"
                          rules={rules.required}
                          breakPoints={{
                            xs: 24,
                            sm: 24,
                            md: 16,
                          }}
                          inputProps={{
                            onPressEnter: () => searchPredio(),
                            onChange: ({ target: { value } }) => {
                              if (!value) {
                                resetAndValidateFact();
                              }
                            },
                          }}
                          resultProps={{
                            breakPoints: {
                              xs: 24,
                              sm: 24,
                              md: 8,
                            },
                            name: 'notificacion',
                            label: 'Notificacion',
                            dataSource: predios,
                            labelProp: 'clave_catastral_municipal',
                            onClear: resetAndValidateFact,
                          }}
                        />
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="clave_catastral"
                            label="clave"
                            rules={rules.required}
                            hasFeedback
                            required
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <NumericInput
                            name="superficie"
                            label="Superficie"
                            required
                            hasFeedback
                          />
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="coordenadas_en_el_mapa"
                            label="coordenadas en el mapa"
                            hasFeedback
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="cuenta_predial"
                            label="cuenta predial"
                            rules={rules.required}
                            hasFeedback
                            required
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Form.Item hidden>
                        <Button htmlType="submit" />
                      </Form.Item>
                    </Form>
                  </TabPane>
                  {addressPermission?.view && (
                    <TabPane
                      tab={(
                        <>
                          <EnvironmentOutlined />
                          Dirección
                        </>
                      )}
                      key="direccion"
                      forceRender
                      disabled={!selectedRowKeys.length}
                    >
                      <Col xs={24} sm={24} md={8}>
                        <Form.Item
                          name="misma_direccion_que_empresa"
                          label="misma direccion de empresa?"
                        >
                          <Select trueFalse />
                        </Form.Item>
                      </Col>
                      <Direccion
                        currentURI={baseURI}
                        parentID={selectedRowKeys[0]}
                        setLoading={setLoading}
                        setFormAddress={setFormAddress}
                        setCurrentTabKey={setCurrentTabKey}
                        addressPropName="direccion"
                        callback={(values, _continue) => {
                          if (!_continue) {
                            onCancel();
                          } else {
                            const [key] = selectedRowKeys;
                            const clone = [...data];
                            const mappedData = clone.map((e) => (e.id === key
                              ? ({ ...e, direccion: values?.id }) : e));
                            setData(mappedData);
                          }
                        }}
                        permission={addressPermission}
                      />
                    </TabPane>
                  )}
                </Tabs>
              </Card>
            </Col>
          )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          visible={visibleAlert}
          content="Factibilidad seleccionada"
          loading={loading}
        />
        <BusquedaAvanzadaPadrones
          visible={visibleBusquedaPadrones}
          setVisible={setVisibleBusquedaPadrones}
          callback={(id) => {
            if (tipoPadron === 2) {
              fetchEmpresa(id);
            } else {
              fetchPredio(id);
            }
          }}
          tipoDePadron={tipoPadron}
        />
      </Spin>
    </Row>
  );
};

export default Factibilidades;
